/**
 * Phase 7 PR 1 (post-smoke fix) — adapter for `<LastSession />` on the
 * real-data path of `/v2/today`.
 *
 * Maps a `RaceResult[]` (from `useRaceResults`) to the `LastSession`
 * card's prop shape. Returns `null` when no race results exist so
 * caller can fall back to the fixture (demo path) or hide the card
 * (real-data path with no race history).
 *
 * Pure — no React, no I/O — fully unit-testable.
 *
 * Why race_results: real session telemetry (heart rate / cadence /
 * splits) requires a wearable-sync hook that doesn't exist yet. The
 * race_results table is the strongest real signal we have for "what
 * was your last serious session" — completed runs with finish times
 * and PR markers. Once a real telemetry hook ships, this adapter can
 * be replaced (or extended) without changing the consumer.
 */

import type { RaceResult } from './racePR';

export interface LastSessionStat {
  value: string;
  label: string;
}

export interface LastSessionData {
  title: string;
  intensityLabel: string;
  stats: LastSessionStat[];
  note: string;
}

/**
 * Pick the most recent race result by date (DESC). The API already
 * returns rows ordered DESC, but we sort defensively so this adapter
 * works on any ordering and the test can use any list shape.
 */
export function pickLatestRace(results: RaceResult[]): RaceResult | null {
  if (!Array.isArray(results) || results.length === 0) return null;
  return [...results].sort((a, b) => {
    if (a.race_date === b.race_date) return 0;
    return a.race_date < b.race_date ? 1 : -1;
  })[0]!;
}

/**
 * Classify the intensity label from the race result. PRs get "PR"
 * (rendered in --signal marine on the card). Non-PR races default to
 * "Race" (it WAS a race, after all). Future extensions can derive
 * from finish_time_seconds against the athlete's recent average.
 */
function classifyIntensity(race: RaceResult): string {
  return race.is_pr ? 'PR' : 'Race';
}

/**
 * Compose the stats row — 2-3 chips. We always include distance +
 * finish time. Place + total_runners is added when both are present
 * (e.g. "4 / 198 place"). Conditions added as the 3rd chip when
 * present and the place isn't (avoid overflowing the 3-chip layout).
 */
function composeStats(race: RaceResult): LastSessionStat[] {
  const stats: LastSessionStat[] = [];
  stats.push({ value: race.distance, label: 'dist' });
  stats.push({ value: race.finish_time, label: 'time' });
  if (race.place !== null && race.total_runners !== null) {
    stats.push({ value: String(race.place), label: `of ${race.total_runners}` });
  } else if (race.place !== null) {
    stats.push({ value: String(race.place), label: 'place' });
  } else if (race.conditions) {
    // Truncate to keep the card layout tight
    stats.push({ value: race.conditions.slice(0, 12), label: 'conds' });
  }
  return stats;
}

/**
 * Pick the note line. Prefer race.notes (athlete's own observation);
 * fall back to a synthesized "New PR" line for PR races without notes;
 * fall back to a generic "Logged." for non-PR races without notes.
 */
function composeNote(race: RaceResult): string {
  if (race.notes && race.notes.trim().length > 0) return race.notes.trim();
  if (race.is_pr) return `New ${race.distance} PR, logged ${race.race_date}.`;
  return `${race.distance} race at ${race.course_name ?? 'an event'}, logged.`;
}

/**
 * Main adapter — race_results → LastSession props (or null when empty).
 */
export function lastSessionFromRaces(
  results: RaceResult[] | null | undefined,
): LastSessionData | null {
  if (!results || results.length === 0) return null;
  const latest = pickLatestRace(results);
  if (!latest) return null;
  return {
    title: `${latest.event_name}`,
    intensityLabel: classifyIntensity(latest),
    stats: composeStats(latest),
    note: composeNote(latest),
  };
}
